"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Maximize2,
  Minimize2,
  Trash2,
  GripVertical,
  Bold,
  Italic,
  List,
  ListOrdered,
  Undo2,
  Redo2,
  CheckSquare,
  StickyNote as StickyNoteIcon,
} from "lucide-react";
import {
  getNote,
  upsertNote,
  deleteNote,
  updateNoteSize,
  type StickyNote as StickyNoteType,
} from "@/lib/stickyNotes";
import { TiptapEditor } from "@/components/editor/TiptapEditor";
import { htmlToDoc, docToHtml, normalizeLegacyStickyHtml } from "@gam/editor";
import type { Editor } from "@tiptap/react";
import type { JSONContent } from "@tiptap/core";

// ─── Open event ──────────────────────────────────────────────

export const STICKY_NOTE_OPEN_EVENT = "cc-sticky-note-open";

export interface StickyNoteOpenDetail {
  elementKey: string;
  elementLabel: string;
  x: number;
  y: number;
}

// ─── Note editor — the shared editor at its "basic" tier ─────
//
// This used to be 100 lines of `contentEditable` over `document.execCommand`:
// a second, private editor with its own bold, its own lists and its own
// checklist built by splicing raw `<input type="checkbox">` into the DOM.
// execCommand has been non-standard and deprecated for years, and none of the
// app's editor behaviour (RTL, gibberish repair, the marks the rest of the
// system understands) reached this surface.
//
// It now mounts the SAME editor every document in the app mounts, at the
// `basic` tier: real extensions, no slash menu, no block handle, no floating
// toolbar — a full-page menu inside a 320×200 note is the wrong thing, not a
// smaller right thing. The amber toolbar below is the note's own, and each
// button calls an editor command instead of execCommand.
//
// Storage is unchanged on purpose: notes stay one HTML string per note in
// localStorage, and `htmlToDoc` / `docToHtml` convert on the edges against
// exactly the extension list this tier mounts. Notes written under the old
// toolbar keep their checkboxes — `normalizeLegacyStickyHtml` maps the old
// raw markup onto real task items before parsing.

interface MiniEditorProps {
  initialContent: string;
  onChange: (html: string) => void;
  autoFocus?: boolean;
}

function MiniEditor({ initialContent, onChange, autoFocus }: MiniEditorProps) {
  const [editor, setEditor] = useState<Editor | null>(null);

  // Parsed once, on mount. The parent holds the HTML in state and feeds it
  // back as `initialContent`, so re-deriving it on every keystroke would hand
  // the editor its own output and fight the caret.
  const [initialDoc] = useState<JSONContent>(() =>
    htmlToDoc(normalizeLegacyStickyHtml(initialContent))
  );

  const handleChange = useCallback(
    (json: JSONContent) => {
      onChange(docToHtml(json));
    },
    [onChange]
  );

  const handleEditorReady = useCallback((e: Editor | null) => setEditor(e), []);

  // Buttons live in the toolbar, so the editor loses focus on mousedown
  // unless it is prevented — the same reason the old toolbar did it.
  const run = useCallback(
    (fn: (chain: ReturnType<Editor["chain"]>) => void) => (e: React.MouseEvent) => {
      e.preventDefault();
      if (!editor) return;
      const chain = editor.chain().focus();
      fn(chain);
    },
    [editor]
  );

  const btn =
    "rounded p-1 text-amber-400/60 hover:text-amber-300 hover:bg-amber-500/10 transition-colors disabled:opacity-40";

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-amber-500/20 bg-amber-950/20 shrink-0">
        <button type="button" disabled={!editor} onMouseDown={run((c) => c.toggleBold().run())} className={btn} title="Bold">
          <Bold className="h-3 w-3" />
        </button>
        <button type="button" disabled={!editor} onMouseDown={run((c) => c.toggleItalic().run())} className={btn} title="Italic">
          <Italic className="h-3 w-3" />
        </button>
        <div className="w-px h-3.5 bg-amber-500/20 mx-0.5" />
        <button type="button" disabled={!editor} onMouseDown={run((c) => c.toggleBulletList().run())} className={btn} title="Bullet List">
          <List className="h-3 w-3" />
        </button>
        <button type="button" disabled={!editor} onMouseDown={run((c) => c.toggleOrderedList().run())} className={btn} title="Numbered List">
          <ListOrdered className="h-3 w-3" />
        </button>
        <div className="w-px h-3.5 bg-amber-500/20 mx-0.5" />
        <button type="button" disabled={!editor} onMouseDown={run((c) => c.undo().run())} className={btn} title="Undo">
          <Undo2 className="h-3 w-3" />
        </button>
        <button type="button" disabled={!editor} onMouseDown={run((c) => c.redo().run())} className={btn} title="Redo">
          <Redo2 className="h-3 w-3" />
        </button>
        <div className="w-px h-3.5 bg-amber-500/20 mx-0.5" />
        <button type="button" disabled={!editor} onMouseDown={run((c) => c.toggleTaskList().run())} className={btn} title="Checklist">
          <CheckSquare className="h-3 w-3" />
        </button>
      </div>
      {/* Editor area — the package's own chrome is off at this tier, so the
          only styling needed here is the note's amber type scale. */}
      <div
        className="flex-1 min-h-0 overflow-y-auto text-xs text-amber-100/80 leading-relaxed
          [&_.gam-editor]:min-h-full [&_.gam-editor]:px-3 [&_.gam-editor]:py-2 [&_.gam-editor]:outline-none
          [&_.gam-editor-wrapper]:h-full
          [&_ul]:list-disc [&_ul]:ms-4 [&_ul]:my-1
          [&_ol]:list-decimal [&_ol]:ms-4 [&_ol]:my-1
          [&_li]:my-0.5
          [&_ul.gam-tasklist]:list-none [&_ul.gam-tasklist]:ms-0
          [&_.gam-taskitem]:flex [&_.gam-taskitem]:items-start [&_.gam-taskitem]:gap-1.5
          [&_input[type=checkbox]]:accent-amber-500"
        style={{ wordBreak: "break-word" }}
      >
        <TiptapEditor
          tier="basic"
          content={initialDoc}
          onChange={handleChange}
          onEditorReady={handleEditorReady}
          autoFocus={autoFocus}
          direction="rtl"
        />
      </div>
    </div>
  );
}

// ─── Sticky Note Popup ───────────────────────────────────────

interface StickyNotePopupProps {
  elementKey: string;
  elementLabel: string;
  initialX: number;
  initialY: number;
  onClose: () => void;
}

function StickyNotePopup({ elementKey, elementLabel, initialX, initialY, onClose }: StickyNotePopupProps) {
  const existing = getNote(elementKey);
  const [content, setContent] = useState(existing?.content || "");
  const [expanded, setExpanded] = useState(false);
  const [size, setSize] = useState({
    width: existing?.width || 320,
    height: existing?.height || 200,
  });
  const [position, setPosition] = useState({ x: initialX, y: initialY });
  const popupRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Adjust position to stay in viewport
  useEffect(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = expanded ? 480 : size.width;
    const h = expanded ? 400 : size.height;
    setPosition({
      x: Math.min(Math.max(8, initialX), vw - w - 8),
      y: Math.min(Math.max(8, initialY), vh - h - 8),
    });
  }, [initialX, initialY, expanded, size.width, size.height]);

  // Auto-save with debounce
  const handleChange = useCallback((html: string) => {
    setContent(html);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      upsertNote(elementKey, elementLabel, html, size);
    }, 500);
  }, [elementKey, elementLabel, size]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Save before closing
        upsertNote(elementKey, elementLabel, content, size);
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, elementKey, elementLabel, content, size]);

  const handleDelete = useCallback(() => {
    deleteNote(elementKey);
    onClose();
  }, [elementKey, onClose]);

  const handleClose = useCallback(() => {
    // Save current content
    if (content.trim() && content !== "<br>") {
      upsertNote(elementKey, elementLabel, content, size);
    }
    onClose();
  }, [content, elementKey, elementLabel, size, onClose]);

  // Drag handlers
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    isDraggingRef.current = true;
    dragOffsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    e.preventDefault();
  }, [position]);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      setPosition({
        x: e.clientX - dragOffsetRef.current.x,
        y: e.clientY - dragOffsetRef.current.y,
      });
    };
    const handleUp = () => { isDraggingRef.current = false; };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  // Resize handler
  const handleResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.width;
    const startH = size.height;

    const onMove = (ev: MouseEvent) => {
      const newW = Math.max(260, startW + (ev.clientX - startX));
      const newH = Math.max(160, startH + (ev.clientY - startY));
      setSize({ width: newW, height: newH });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // Persist size
      setSize((s) => {
        updateNoteSize(elementKey, s.width, s.height);
        return s;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [size, elementKey]);

  const w = expanded ? 480 : size.width;
  const h = expanded ? 400 : size.height;

  return (
    <div
      ref={popupRef}
      className="fixed z-[90] flex flex-col rounded-lg border border-amber-500/30 bg-gradient-to-b from-amber-950/95 to-slate-900/95 shadow-2xl shadow-amber-900/20 backdrop-blur-sm overflow-hidden"
      style={{
        left: position.x,
        top: position.y,
        width: w,
        height: h,
      }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
    >
      {/* Header — draggable */}
      <div
        className="flex items-center gap-2 px-2.5 py-1.5 border-b border-amber-500/20 cursor-move select-none shrink-0"
        onMouseDown={handleDragStart}
      >
        <GripVertical className="h-3 w-3 text-amber-500/40" />
        <StickyNoteIcon className="h-3 w-3 text-amber-400" />
        <span className="flex-1 text-[11px] font-medium text-amber-300 truncate">
          {elementLabel}
        </span>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="rounded p-0.5 text-amber-400/50 hover:text-amber-300 transition-colors"
          title={expanded ? "Minimize" : "Maximize"}
        >
          {expanded ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
        </button>
        <button
          type="button"
          onClick={handleDelete}
          className="rounded p-0.5 text-amber-400/50 hover:text-red-400 transition-colors"
          title="Delete note"
        >
          <Trash2 className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={handleClose}
          className="rounded p-0.5 text-amber-400/50 hover:text-amber-200 transition-colors"
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      {/* Editor */}
      <MiniEditor
        initialContent={content}
        onChange={handleChange}
        autoFocus
      />

      {/* Resize handle */}
      {!expanded && (
        <div
          className="absolute bottom-0 end-0 w-4 h-4 cursor-se-resize"
          onMouseDown={handleResize}
        >
          <svg className="w-3 h-3 text-amber-500/30 absolute bottom-0.5 end-0.5" viewBox="0 0 12 12">
            <path d="M11 1v10H1" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path d="M11 5v6H5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </div>
      )}
    </div>
  );
}

// ─── Sticky Notes Manager (renders all open notes) ───────────

export function StickyNotesManager() {
  const [openNotes, setOpenNotes] = useState<StickyNoteOpenDetail[]>([]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<StickyNoteOpenDetail>).detail;
      setOpenNotes((prev) => {
        // If already open for this element, just focus it (remove and re-add)
        const filtered = prev.filter((n) => n.elementKey !== detail.elementKey);
        return [...filtered, detail];
      });
    };
    window.addEventListener(STICKY_NOTE_OPEN_EVENT, handler);
    return () => window.removeEventListener(STICKY_NOTE_OPEN_EVENT, handler);
  }, []);

  const handleClose = useCallback((elementKey: string) => {
    setOpenNotes((prev) => prev.filter((n) => n.elementKey !== elementKey));
  }, []);

  return (
    <>
      {openNotes.map((note) => (
        <StickyNotePopup
          key={note.elementKey}
          elementKey={note.elementKey}
          elementLabel={note.elementLabel}
          initialX={note.x}
          initialY={note.y}
          onClose={() => handleClose(note.elementKey)}
        />
      ))}
    </>
  );
}
